import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

interface DirectoryQueryParams {
  statusFilter: string;
  terminalFilter: string;
  staffingFilter: string;
  search: string;
}

interface DirectoryResponse {
  error?: string;
  employees?: unknown[];
  terminals?: unknown[];
  grouped?: Record<string, unknown[]>;
  unassigned?: unknown[];
  caller_permissions?: Record<string, unknown>;
}

function buildQuery({ statusFilter, terminalFilter, staffingFilter, search }: DirectoryQueryParams) {
  const params = new URLSearchParams();

  if (statusFilter) params.set('status', statusFilter);
  if (terminalFilter) params.set('terminal_id', terminalFilter);
  if (staffingFilter) params.set('staffing_type', staffingFilter);
  if (search) params.set('search', search);

  const query = params.toString();
  return `/api/directory${query ? `?${query}` : ''}`;
}

export function useDirectoryRead() {
  const [employees, setEmployees] = useState<unknown[]>([]);
  const [terminals, setTerminals] = useState<unknown[]>([]);
  const [grouped, setGrouped] = useState<Record<string, unknown[]>>({});
  const [unassigned, setUnassigned] = useState<unknown[]>([]);
  const [permissions, setPermissions] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [terminalFilter, setTerminalFilter] = useState('');
  const [staffingFilter, setStaffingFilter] = useState('');

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function loadDirectory(current: Partial<DirectoryQueryParams> = {}) {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<DirectoryResponse>(buildQuery({
        statusFilter: current.statusFilter ?? statusFilter,
        terminalFilter: current.terminalFilter ?? terminalFilter,
        staffingFilter: current.staffingFilter ?? staffingFilter,
        search: current.search ?? search,
      }));

      if (data.error) {
        setError(data.error);
        setEmployees([]);
        setTerminals([]);
        setGrouped({});
        setUnassigned([]);
        setPermissions({});
        return;
      }

      setEmployees(data.employees || []);
      setTerminals(data.terminals || []);
      setGrouped(data.grouped || {});
      setUnassigned(data.unassigned || []);
      setPermissions(data.caller_permissions || {});
    } catch (err) {
      setError((err as Error).message || 'Directory load failed');
      setEmployees([]);
      setTerminals([]);
      setGrouped({});
      setUnassigned([]);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDirectory();
  }, [statusFilter, terminalFilter, staffingFilter]);

  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  function handleSearch(nextValue: string) {
    setSearch(nextValue);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      loadDirectory({ search: nextValue });
    }, 300);
  }

  const groupedTerminalIds = useMemo(() => Object.keys(grouped), [grouped]);

  return {
    employees,
    terminals,
    grouped,
    groupedTerminalIds,
    unassigned,
    permissions,
    loading,
    error,
    search,
    statusFilter,
    terminalFilter,
    staffingFilter,
    setStatusFilter,
    setTerminalFilter,
    setStaffingFilter,
    handleSearch,
    reload: loadDirectory,
  };
}
